import { ReactElement, useEffect, useState, useRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker, { registerLocale } from 'react-datepicker';
import en from 'date-fns/locale/en-US';
import { TimeSpanDateAttributes } from '../constants/telosair-interfaces';
import Button from './Button';

type Props = {
  handleCancel: () => void;
  onApply: (value: TimeSpanDateAttributes) => void;
  timeSpanDate: TimeSpanDateAttributes | null;
  minDate?: Date | null;
};

// Register locale to be used by react-datepicker
registerLocale('en', en);

/**
 * A dropdown react component.
 * @param props - DropdownProps.
 * @returns - React element to capture user selection.
 */
export default function DateRangePicker(props: Props): ReactElement {
  const { handleCancel, onApply, timeSpanDate, minDate } = props;

  const [startDate, setStartDate] = useState<Date | null>(
    timeSpanDate ? new Date(timeSpanDate.startDate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    timeSpanDate ? new Date(timeSpanDate.endDate) : null
  );

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleStartDateChange = (date: Date): void => {
    setStartDate(date);
    // Clear end date if it's before the start date
    if (endDate && date > endDate) {
      setEndDate(null);
    }
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleEndDateChange = (date: Date): void => {
    setEndDate(date);
  };

  /**
   * A function to select date range.
   */
  function handleApply(): void {
    if (startDate && endDate) {
      const value = {
        startDate: new Date(startDate).getTime(),
        endDate: new Date(endDate).getTime()
      };
      onApply(value);
    }
  }

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line jsdoc/require-jsdoc
    function handleClickOutside(event: MouseEvent): void {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        // Check if the click is inside a react-datepicker portal/popper (dropdown portals, timezone options, etc)
        const target = event.target as HTMLElement;
        if (
          !target.closest('.react-datepicker') &&
          !target.closest('.react-datepicker__portal') &&
          !target.closest('.react-datepicker-popper')
        ) {
          handleCancel();
        }
      }
    }

    // Use 'click' rather than 'mousedown' to make sure click events on times/dropdowns bubble properly
    // and don't prematurely fire as outside clicks
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleCancel]);

  return (
    <div
      ref={wrapperRef}
      className="bg-container-700 p-3 px-5 flex flex-col justify-center items-center react-datepicker-ignore-onclickoutside rounded"
    >
      <div className="flex gap-3">
        <div className="gap-1 flex flex-col">
          <div className="text-gray-300 text-xs font-semibold">Start Date</div>
          <DatePicker
            selectsStart
            showTimeSelect
            locale="en"
            inline // Keep the calendar always open
            selected={startDate}
            startDate={startDate}
            endDate={endDate}
            minDate={minDate ?? undefined}
            maxDate={new Date()}
            onChange={handleStartDateChange}
            placeholderText="Start Date"
            className="flex"
            popperPlacement="bottom-start"
          />
        </div>
        <div className="gap-1 flex flex-col">
          <div className="text-gray-300 text-xs font-semibold">End Date</div>
          <DatePicker
            selectsEnd
            showTimeSelect
            locale="en"
            inline // Keep the calendar always open
            selected={endDate}
            startDate={startDate}
            endDate={endDate}
            onChange={handleEndDateChange}
            maxDate={new Date()}
            placeholderText="End Date"
            minDate={startDate} // Ensures end date is after start date
          />
        </div>
      </div>
      <div className="mt-4 flex gap-3 w-full justify-end">
        {/* Apply and Cancel Buttons */}
        <Button onClick={() => handleCancel()} size="sm" theme="secondary-gray" name="Cancel" />
        <Button onClick={() => handleApply()} size="sm" theme="primary" name="Apply" />
      </div>
    </div>
  );
}
